import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SiteLayout } from "../components/layout/SiteLayout";
import { faqPageJsonLd, SeoHead } from "../components/seo/SeoHead";
import { usePepI18n } from "../i18n/usePepI18n";
import {
  KNOWLEDGE_COMPARE_ROWS,
  KNOWLEDGE_DEPLOYMENT_ASSET,
  KNOWLEDGE_DEPLOYMENT_MODELS,
  KNOWLEDGE_FAQ,
  KNOWLEDGE_FAQ_CATEGORIES,
  KNOWLEDGE_TRUST_PILLS,
  KNOWLEDGE_WHY_POINTS,
  knowledgeCopy,
  type KnowledgeFaqCategory,
} from "../knowledge/knowledge-content";

/**
 * Knowledge Center — WP-3 (Deployment Models + General FAQ).
 * Reuses SiteLayout, SeoHead, pep-page-hero / card patterns. No new design system.
 */
export function KnowledgePage() {
  const { locale } = usePepI18n();
  const en = locale === "en";
  const copy = knowledgeCopy(locale);
  const [faqCategory, setFaqCategory] = useState<KnowledgeFaqCategory>("all");
  const [faqQuery, setFaqQuery] = useState("");
  const [openFaqId, setOpenFaqId] = useState<string | null>(KNOWLEDGE_FAQ[0]?.id ?? null);

  const filteredFaq = useMemo(() => {
    const q = faqQuery.trim().toLowerCase();
    return KNOWLEDGE_FAQ.filter((item) => {
      if (faqCategory !== "all" && item.category !== faqCategory) return false;
      if (!q) return true;
      const hay = `${en ? item.qEn : item.qFr} ${en ? item.aEn : item.aFr}`.toLowerCase();
      return hay.includes(q);
    });
  }, [en, faqCategory, faqQuery]);

  const jsonLd = useMemo(
    () =>
      faqPageJsonLd({
        canonicalPath: "/knowledge",
        name: copy.seoTitle,
        description: copy.seoDescription,
        questions: KNOWLEDGE_FAQ.map((item) => ({
          question: en ? item.qEn : item.qFr,
          answer: en ? item.aEn : item.aFr,
        })),
      }),
    [copy.seoDescription, copy.seoTitle, en],
  );

  return (
    <SiteLayout>
      <SeoHead
        title={copy.seoTitle}
        description={copy.seoDescription}
        keywords={[...copy.seoKeywords]}
        canonicalPath="/knowledge"
        imageUrl={KNOWLEDGE_DEPLOYMENT_ASSET.src}
        locale={locale}
        jsonLd={jsonLd}
      />

      {/* 1. Hero */}
      <header className="pep-page-hero pep-knowledge-hero py-5 mb-0" data-section="K-HERO">
        <div className="container">
          <div className="row align-items-center g-4">
            <div className="col-lg-8">
              <p className="pep-knowledge-eyebrow mb-2">{copy.heroBadge}</p>
              <h1 className="display-5 fw-bold mb-3">{copy.heroH1}</h1>
              <p className="lead opacity-90 mb-4 col-xl-10">{copy.heroLead}</p>
              <div className="pep-knowledge-hero__ctas d-flex flex-wrap gap-2">
                <Link className="btn pep-pd-btn-primary" to={copy.heroPrimaryHref}>
                  {copy.heroPrimaryCta}
                </Link>
                <Link className="btn pep-pd-btn-ghost" to={copy.heroSecondaryHref}>
                  {copy.heroSecondaryCta}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Section tabs (in-page anchors) */}
      <nav className="pep-knowledge-tabs" aria-label={copy.heroBadge}>
        <div className="container">
          <ul className="pep-knowledge-tabs__list list-unstyled mb-0">
            <li>
              <a className="pep-knowledge-tabs__link pep-knowledge-tabs__link--active" href="#deployment-models">
                {copy.tabDeploy}
              </a>
            </li>
            <li>
              <a className="pep-knowledge-tabs__link" href="#faq">
                {copy.tabFaq}
              </a>
            </li>
            <li>
              <a className="pep-knowledge-tabs__link" href="#compare">
                {copy.tabCompare}
              </a>
            </li>
          </ul>
        </div>
      </nav>

      {/* 2. Deployment Models — text left, HD asset right */}
      <section
        id="deployment-models"
        className="pep-knowledge-section pep-knowledge-models"
        aria-labelledby="knowledge-models-h2"
        data-section="K-MODELS"
      >
        <div className="container">
          <div className="row g-4 g-xl-5 align-items-start">
            <div className="col-lg-6 pep-knowledge-models__copy">
              <p className="pep-knowledge-eyebrow pep-knowledge-eyebrow--ink mb-2">{copy.modelsBadge}</p>
              <h2 id="knowledge-models-h2" className="pep-knowledge-h2">
                {copy.modelsH2}
              </h2>
              <p className="pep-knowledge-lead">{copy.modelsLead}</p>

              <ul className="pep-knowledge-model-grid list-unstyled mb-4">
                {KNOWLEDGE_DEPLOYMENT_MODELS.map((model) => (
                  <li key={model.id} className="pep-knowledge-model-card">
                    <div className="pep-knowledge-model-card__head">
                      <h3 className="pep-knowledge-model-card__title">
                        {en ? model.titleEn : model.titleFr}
                      </h3>
                      <span
                        className={
                          model.activeV1
                            ? "pep-knowledge-pill pep-knowledge-pill--active"
                            : "pep-knowledge-pill"
                        }
                      >
                        {model.activeV1 ? copy.modelsActiveBadge : copy.modelsContractBadge}
                      </span>
                    </div>
                    <p className="pep-knowledge-model-card__body mb-2">
                      {en ? model.bodyEn : model.bodyFr}
                    </p>
                    <Link className="pep-knowledge-model-card__link" to={model.href}>
                      {copy.modelsLearnMore}
                    </Link>
                  </li>
                ))}
              </ul>

              <Link className="btn pep-pd-btn-primary" to={copy.modelsCtaHref}>
                {copy.modelsCta}
              </Link>

              <ul className="pep-knowledge-trust list-unstyled mb-0 mt-4">
                {KNOWLEDGE_TRUST_PILLS.map((pill) => (
                  <li key={pill.id} className="pep-knowledge-trust__item">
                    {en ? pill.labelEn : pill.labelFr}
                  </li>
                ))}
              </ul>
            </div>

            <div className="col-lg-6 pep-knowledge-models__visual-col">
              <figure className="pep-knowledge-models__figure mb-0">
                <div className="pep-knowledge-models__glow" aria-hidden="true" />
                <picture>
                  <img
                    className="pep-knowledge-models__img"
                    src={KNOWLEDGE_DEPLOYMENT_ASSET.src}
                    alt={en ? KNOWLEDGE_DEPLOYMENT_ASSET.altEn : KNOWLEDGE_DEPLOYMENT_ASSET.altFr}
                    width={KNOWLEDGE_DEPLOYMENT_ASSET.width}
                    height={KNOWLEDGE_DEPLOYMENT_ASSET.height}
                    loading="lazy"
                    decoding="async"
                  />
                </picture>
              </figure>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Why several modes */}
      <section
        className="pep-knowledge-section pep-knowledge-section--muted"
        aria-labelledby="knowledge-why-h2"
        data-section="K-WHY"
      >
        <div className="container">
          <header className="pep-knowledge-section__head text-center mx-auto">
            <h2 id="knowledge-why-h2" className="pep-knowledge-h2">
              {copy.whyH2}
            </h2>
            <p className="pep-knowledge-lead">{copy.whyLead}</p>
          </header>
          <ul className="pep-knowledge-why-grid list-unstyled mb-0">
            {KNOWLEDGE_WHY_POINTS.map((point) => (
              <li key={point.id} className="pep-knowledge-why-card">
                <h3 className="pep-knowledge-why-card__title">
                  {en ? point.titleEn : point.titleFr}
                </h3>
                <p className="mb-0">{en ? point.bodyEn : point.bodyFr}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* 4. Comparison */}
      <section
        id="compare"
        className="pep-knowledge-section"
        aria-labelledby="knowledge-compare-h2"
        data-section="K-COMPARE"
      >
        <div className="container">
          <header className="pep-knowledge-section__head text-center mx-auto">
            <h2 id="knowledge-compare-h2" className="pep-knowledge-h2">
              {copy.compareH2}
            </h2>
            <p className="pep-knowledge-lead">{copy.compareLead}</p>
          </header>
          <div className="pep-knowledge-compare-wrap">
            <table className="pep-knowledge-compare table align-middle mb-0">
              <thead>
                <tr>
                  <th scope="col">{copy.compareCriterion}</th>
                  <th scope="col">{copy.compareSaas}</th>
                  <th scope="col">{copy.comparePrivate}</th>
                  <th scope="col">{copy.compareOnPrem}</th>
                  <th scope="col">{copy.compareHybrid}</th>
                  <th scope="col">{copy.comparePartner}</th>
                </tr>
              </thead>
              <tbody>
                {KNOWLEDGE_COMPARE_ROWS.map((row) => (
                  <tr key={row.id}>
                    <th scope="row">{en ? row.criterionEn : row.criterionFr}</th>
                    <td>{en ? row.saasEn : row.saasFr}</td>
                    <td>{en ? row.privateEn : row.privateFr}</td>
                    <td>{en ? row.onpremEn : row.onpremFr}</td>
                    <td>{en ? row.hybridEn : row.hybridFr}</td>
                    <td>{en ? row.partnerEn : row.partnerFr}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 5. FAQ */}
      <section
        id="faq"
        className="pep-knowledge-section pep-knowledge-section--muted"
        aria-labelledby="knowledge-faq-h2"
        data-section="K-FAQ"
      >
        <div className="container">
          <header className="pep-knowledge-section__head text-center mx-auto">
            <h2 id="knowledge-faq-h2" className="pep-knowledge-h2">
              {copy.faqH2}
            </h2>
            <p className="pep-knowledge-lead">{copy.faqLead}</p>
          </header>

          <div className="pep-knowledge-faq-toolbar mx-auto">
            <label className="visually-hidden" htmlFor="knowledge-faq-search">
              {copy.faqSearchPlaceholder}
            </label>
            <input
              id="knowledge-faq-search"
              className="form-control pep-knowledge-faq-search"
              type="search"
              value={faqQuery}
              onChange={(e) => setFaqQuery(e.target.value)}
              placeholder={copy.faqSearchPlaceholder}
            />
            <div className="pep-knowledge-faq-cats" role="group" aria-label={copy.faqH2}>
              {KNOWLEDGE_FAQ_CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  className={
                    faqCategory === cat.id
                      ? "pep-knowledge-faq-cat pep-knowledge-faq-cat--active"
                      : "pep-knowledge-faq-cat"
                  }
                  onClick={() => setFaqCategory(cat.id)}
                >
                  {en ? cat.labelEn : cat.labelFr}
                </button>
              ))}
            </div>
          </div>

          <div className="pep-knowledge-faq-list mx-auto">
            {filteredFaq.length === 0 ? (
              <p className="text-secondary text-center mb-0">{copy.faqEmpty}</p>
            ) : (
              filteredFaq.map((item) => {
                const open = openFaqId === item.id;
                return (
                  <div key={item.id} className="pep-knowledge-faq-item">
                    <button
                      type="button"
                      className="pep-knowledge-faq-item__q"
                      aria-expanded={open}
                      onClick={() => setOpenFaqId(open ? null : item.id)}
                    >
                      <span>{en ? item.qEn : item.qFr}</span>
                      <span className="pep-knowledge-faq-item__chev" aria-hidden="true">
                        {open ? "−" : "+"}
                      </span>
                    </button>
                    {open ? (
                      <div className="pep-knowledge-faq-item__a">
                        <p className="mb-0">{en ? item.aEn : item.aFr}</p>
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>

          <div className="pep-knowledge-faq-support text-center mt-4">
            <p className="fw-semibold mb-2">{copy.faqSupportTitle}</p>
            <Link className="btn pep-pd-btn-primary" to={copy.faqSupportHref}>
              {copy.faqSupportCta}
            </Link>
          </div>
        </div>
      </section>

      {/* 6. Final CTA */}
      <section className="pep-cta-band pep-knowledge-cta" data-section="K-CTA" aria-labelledby="knowledge-cta-h2">
        <div className="container text-center py-5">
          <h2 id="knowledge-cta-h2" className="h3 fw-bold mb-3">
            {copy.ctaH2}
          </h2>
          <p className="mb-4 opacity-90 col-lg-8 mx-auto">{copy.ctaLead}</p>
          <div className="d-flex flex-wrap gap-2 justify-content-center">
            <Link className="btn btn-light" to={copy.ctaPrimaryHref}>
              {copy.ctaPrimary}
            </Link>
            <Link className="btn btn-outline-light" to={copy.ctaSecondaryHref}>
              {copy.ctaSecondary}
            </Link>
          </div>
          <nav className="mt-4" aria-label={en ? "Related pages" : "Pages associées"}>
            <ul className="list-inline small mb-0 opacity-90">
              <li className="list-inline-item">
                <Link className="link-light" to="/products">
                  {en ? "Products" : "Produits"}
                </Link>
              </li>
              <li className="list-inline-item">·</li>
              <li className="list-inline-item">
                <Link className="link-light" to="/solutions">
                  {en ? "Solutions" : "Solutions"}
                </Link>
              </li>
              <li className="list-inline-item">·</li>
              <li className="list-inline-item">
                <Link className="link-light" to="/industries">
                  {en ? "Industries" : "Secteurs"}
                </Link>
              </li>
              <li className="list-inline-item">·</li>
              <li className="list-inline-item">
                <Link className="link-light" to="/why-itaas">
                  {en ? "Why ITaaS" : "Pourquoi ITaaS"}
                </Link>
              </li>
              <li className="list-inline-item">·</li>
              <li className="list-inline-item">
                <Link className="link-light" to="/configure">
                  {en ? "Configure" : "Configurer"}
                </Link>
              </li>
              <li className="list-inline-item">·</li>
              <li className="list-inline-item">
                <Link className="link-light" to="/contact">
                  {en ? "Contact" : "Contact"}
                </Link>
              </li>
            </ul>
          </nav>
        </div>
      </section>
    </SiteLayout>
  );
}
